// ATTRIBUTION ENGINE — counterfactual baseline discipline.
//
// The question this answers is NOT "did we hit our plan?" (that is the drift
// kernel: actual vs the committed realization curve). It is the strictly harder
// question the renewal conversation turns on: **"did the action CAUSE the
// improvement?"** — which is unanswerable without a counterfactual: *versus what
// would have happened had we done nothing.*
//
// The governed counterfactual contract, applied here:
//   - The counterfactual is the momentum / do-nothing projection FROM THE
//     COMMITMENT DATE. The projection formula already exists
//     (`finance_momentum_projection@1.0.0`, "projected from the latest observed
//     value without strategy ambition or lever effects"). This module does not
//     re-derive momentum; it consumes a projection **pinned as-of the decision** —
//     the read-live-freeze-at-decision law applied to attribution — and records
//     which formula produced it and the as-of it was frozen at.
//   - Attributed impact = actual − counterfactual. Because the counterfactual is
//     itself uncertain (a Conservative/Base/Stretch projection band, per the
//     scenario-disclosure law), that uncertainty is CARRIED into the attribution:
//     the counterfactual's band becomes the attribution's honesty band, so the
//     answer is "improvement attributable to the action: $X to $Y (base $Z),
//     scenario-labeled" and never one false-precision point value.
//   - Negative attributed impact is a first-class, honest state: if do-nothing
//     momentum would have delivered the gain anyway, the action's attributed
//     credit is small or negative, and the engine says so rather than clamping.
//
// This is a pure `packages/lib` contract + deterministic compute (no engine, no
// I/O), mirroring `ma-synergy-commitment.ts`. Confound honesty (temporal overlap
// of multiple actions on one metric window) is layered on top in
// `attribution-confounds.ts`; the outcome shape here reserves the fields it fills.

export const ATTRIBUTION_COUNTERFACTUAL_SCHEMA_VERSION = 'attribution_counterfactual_v1' as const;
export type AttributionCounterfactualSchemaVersion =
  typeof ATTRIBUTION_COUNTERFACTUAL_SCHEMA_VERSION;

// Scenario-disclosure vocabulary — identical axis to SynergyProjectionApprovedBand
// (ma-synergy-commitment.ts) and the walk-forward backtest fan (scripts/
// backtest-walkforward-v2/scorer.mjs), so a counterfactual band is scored by the
// same conservative<=base<=stretch containment discipline used everywhere else.
export const ATTRIBUTION_SCENARIO_BANDS = ['conservative', 'base', 'stretch'] as const;
export type AttributionScenarioBand = (typeof ATTRIBUTION_SCENARIO_BANDS)[number];

// How the do-nothing projection was produced. `momentum_projection` pins an
// approved momentum formula; `flat_hold` is the maximally-conservative do-nothing
// (the baseline held flat); `external_supplied` carries a caller-governed band
// with its own provenance.
export const COUNTERFACTUAL_METHODS = [
  'momentum_projection',
  'flat_hold',
  'external_supplied',
] as const;
export type CounterfactualMethod = (typeof COUNTERFACTUAL_METHODS)[number];

// Attribution only reasons over directional metrics: 'increase' (higher is the
// improvement, e.g. revenue, headcount added) or 'decrease' (lower is the
// improvement, e.g. cost/vendor-spend/headcount takeout). 'hold' metrics have no
// signed improvement to attribute and are rejected as a named gap.
export type AttributionMetricDirection = 'increase' | 'decrease';

export type AttributionWindow = Readonly<{
  from: string;
  to: string;
}>;

// The do-nothing metric value at the evaluation horizon, as a Conservative/Base/
// Stretch band. Ordered by numeric value: conservativeValue <= baseValue <=
// stretchValue (enforced).
export type CounterfactualProjectionBand = Readonly<{
  conservativeValue: number;
  baseValue: number;
  stretchValue: number;
}>;

export type CounterfactualProvenance = Readonly<{
  sourceClass: string;
  evidenceRefIds: readonly string[];
  sourceArtifactId: string | null;
}>;

export type CounterfactualBasis = Readonly<{
  method: CounterfactualMethod;
  // The pinned projection formula — read-live-freeze-at-decision made explicit.
  formulaId: string | null;
  formulaVersion: string | null;
  formulaRef: string | null;
  // The commitment date the projection is frozen at (the decision as-of).
  pinnedAsOf: string;
  // The as-of the projection is evaluated to (the actual observation date).
  horizonAsOf: string;
  projected: CounterfactualProjectionBand;
  bandBasis: string;
  provenance: CounterfactualProvenance;
}>;

// The attribution honesty band — attributed impact in USD, Conservative<=Base<=
// Stretch. May be negative (momentum would have delivered it anyway).
export type AttributionBand = Readonly<{
  conservativeUsd: number;
  baseUsd: number;
  stretchUsd: number;
}>;

// Reconciliation property, the same governance contract as variance_decomposition:
// the parts reconstruct the whole with a named residual. Here: the base
// counterfactual plus the base attributed metric-unit delta reconstructs the
// observed actual (in metric units) exactly.
export type AttributionReconciliation = Readonly<{
  actualValue: number;
  counterfactualBaseValue: number;
  attributedBaseDeltaUnits: number;
  reconstructedActual: number;
  residual: number;
  tolerance: number;
  reconciles: boolean;
}>;

// A confound that overlaps the attribution window (populated by
// attribution-confounds.ts; empty for a clean attribution).
export type ConfoundDeclaration = Readonly<{
  eventId: string;
  label: string;
  kind: 'overlapping_action' | 'regime_shift' | 'external_condition';
  overlapWindow: AttributionWindow;
  evidenceRefIds: readonly string[];
}>;

export type AttributionStatus = 'attributed' | 'confounded' | 'gap';

export type AttributedOutcomeProvenance = Readonly<{
  commitmentEvidenceRefIds: readonly string[];
  actualsEvidenceRefIds: readonly string[];
  counterfactualEvidenceRefIds: readonly string[];
}>;

export type AttributedOutcome = Readonly<{
  schemaVersion: AttributionCounterfactualSchemaVersion;
  commitmentId: string;
  metricKey: string;
  unit: string;
  direction: AttributionMetricDirection;
  commitmentDate: string;
  asOf: string;
  attributionWindow: AttributionWindow;
  actualValue: number;
  valuePerUnitUsd: number;
  annualizationFactor: number;
  counterfactual: CounterfactualBasis;
  attributedImpact: AttributionBand;
  attributionBandBasis: string;
  reconciliation: AttributionReconciliation;
  confounds: readonly ConfoundDeclaration[];
  cleanWindow: AttributionWindow | null;
  attributionStatus: AttributionStatus;
  provenance: AttributedOutcomeProvenance;
  verdictSentence: string;
}>;

export type AttributionGapReasonCode =
  | 'invalid_input'
  | 'invalid_direction'
  | 'missing_counterfactual_basis'
  | 'invalid_counterfactual_band_order'
  | 'invalid_value_binding'
  | 'non_finite_input'
  | 'attribution_reconciliation_failure';

export type AttributionGap = Readonly<{
  status: 'gap';
  commitmentId: string;
  metricKey: string;
  reasonCode: AttributionGapReasonCode;
  missingKeys: readonly string[];
  message: string;
}>;

export type ComputeCounterfactualAttributionResult =
  | Readonly<{ status: 'ok'; outcome: AttributedOutcome }>
  | AttributionGap;

export type ComputeCounterfactualAttributionInput = Readonly<{
  commitmentId: string;
  metricKey: string;
  unit: string;
  direction: AttributionMetricDirection;
  commitmentDate: string;
  // USD per one metric unit of improvement (same value-per-unit contract the drift
  // kernel uses to turn behind-plan units into dollars). Must be > 0.
  valuePerUnitUsd: number;
  // Annualization multiplier for run-rate framing (default 1). Must be > 0.
  annualizationFactor?: number;
  actual: Readonly<{
    value: number;
    asOf: string;
    evidenceRefIds: readonly string[];
  }>;
  counterfactual: CounterfactualBasis;
  commitmentEvidenceRefIds: readonly string[];
  tolerance?: number;
}>;

const DEFAULT_TOLERANCE = 0.01;

export function computeCounterfactualAttribution(
  input: ComputeCounterfactualAttributionInput,
): ComputeCounterfactualAttributionResult {
  const commitmentId = typeof input?.commitmentId === 'string' ? input.commitmentId : '';
  const metricKey = typeof input?.metricKey === 'string' ? input.metricKey : '';

  if (!commitmentId || !metricKey) {
    return gap(
      commitmentId,
      metricKey,
      'invalid_input',
      ['commitmentId', 'metricKey'],
      'Attribution requires a commitmentId and metricKey.',
    );
  }

  if (input.direction !== 'increase' && input.direction !== 'decrease') {
    return gap(
      commitmentId,
      metricKey,
      'invalid_direction',
      ['direction'],
      'Attribution requires a directional metric (increase or decrease); a hold metric has no signed improvement to attribute.',
    );
  }

  const counterfactual = input.counterfactual;
  if (!counterfactual || !isRecord(counterfactual.projected)) {
    return gap(
      commitmentId,
      metricKey,
      'missing_counterfactual_basis',
      ['counterfactual.projected'],
      'Attribution requires a counterfactual (do-nothing) projection pinned as-of the commitment date.',
    );
  }

  const annualizationFactor =
    input.annualizationFactor === undefined ? 1 : input.annualizationFactor;
  if (
    !isFiniteNumber(input.valuePerUnitUsd) ||
    input.valuePerUnitUsd <= 0 ||
    !isFiniteNumber(annualizationFactor) ||
    annualizationFactor <= 0
  ) {
    return gap(
      commitmentId,
      metricKey,
      'invalid_value_binding',
      ['valuePerUnitUsd', 'annualizationFactor'],
      'Attribution cannot convert a metric-unit improvement to dollars without a positive value-per-unit and annualization factor.',
    );
  }

  const { conservativeValue, baseValue, stretchValue } = counterfactual.projected;
  const actualValue = input.actual?.value;
  if (
    !isFiniteNumber(actualValue) ||
    !isFiniteNumber(conservativeValue) ||
    !isFiniteNumber(baseValue) ||
    !isFiniteNumber(stretchValue)
  ) {
    return gap(
      commitmentId,
      metricKey,
      'non_finite_input',
      ['actual.value', 'counterfactual.projected'],
      'Attribution requires finite actual and counterfactual band values.',
    );
  }

  if (!(conservativeValue <= baseValue && baseValue <= stretchValue)) {
    return gap(
      commitmentId,
      metricKey,
      'invalid_counterfactual_band_order',
      ['counterfactual.projected'],
      'The counterfactual projection band must be ordered conservativeValue <= baseValue <= stretchValue.',
    );
  }

  // Direction-aware attributed metric-unit delta over each counterfactual band
  // point. increase: credit = actual − counterfactual (a higher do-nothing means
  // less credit). decrease: credit = counterfactual − actual (a higher do-nothing
  // cost means more credit for having cut it).
  const deltaUnits = (cfValue: number): number =>
    input.direction === 'increase' ? actualValue - cfValue : cfValue - actualValue;

  const toUsd = (units: number): number =>
    round(units * input.valuePerUnitUsd * annualizationFactor, 2);

  // The three attributed-dollar points. The band-extreme counterfactuals map to
  // the attribution extremes; the base counterfactual maps to the base attribution
  // (the mapping is monotone, so base always lands between the extremes).
  const usdAtConservativeCf = toUsd(deltaUnits(conservativeValue));
  const usdAtBaseCf = toUsd(deltaUnits(baseValue));
  const usdAtStretchCf = toUsd(deltaUnits(stretchValue));

  const extremes = [usdAtConservativeCf, usdAtStretchCf].sort((a, b) => a - b);
  const attributedImpact: AttributionBand = Object.freeze({
    conservativeUsd: extremes[0],
    baseUsd: usdAtBaseCf,
    stretchUsd: extremes[1],
  });

  const attributedBaseDeltaUnits = round(deltaUnits(baseValue), 6);
  const reconstructedActual =
    input.direction === 'increase'
      ? round(baseValue + attributedBaseDeltaUnits, 6)
      : round(baseValue - attributedBaseDeltaUnits, 6);
  const tolerance = isFiniteNumber(input.tolerance) ? input.tolerance : DEFAULT_TOLERANCE;
  const residual = round(actualValue - reconstructedActual, 6);
  const reconciles = Math.abs(residual) <= tolerance;

  if (!reconciles) {
    return gap(
      commitmentId,
      metricKey,
      'attribution_reconciliation_failure',
      ['reconciliation.residual'],
      `Attribution does not reconcile; the base counterfactual plus attributed delta does not reconstruct the actual (residual=${residual}).`,
    );
  }

  const attributionWindow: AttributionWindow = Object.freeze({
    from: input.commitmentDate,
    to: input.actual.asOf,
  });

  const reconciliation: AttributionReconciliation = Object.freeze({
    actualValue,
    counterfactualBaseValue: baseValue,
    attributedBaseDeltaUnits,
    reconstructedActual,
    residual,
    tolerance,
    reconciles,
  });

  const provenance: AttributedOutcomeProvenance = Object.freeze({
    commitmentEvidenceRefIds: dedupe(input.commitmentEvidenceRefIds),
    actualsEvidenceRefIds: dedupe(input.actual.evidenceRefIds),
    counterfactualEvidenceRefIds: dedupe(counterfactual.provenance?.evidenceRefIds ?? []),
  });

  const attributionBandBasis = buildAttributionBandBasis(counterfactual);
  const verdictSentence = buildVerdictSentence({
    attributedImpact,
    actualValue,
    counterfactual,
    direction: input.direction,
  });

  const outcome: AttributedOutcome = Object.freeze({
    schemaVersion: ATTRIBUTION_COUNTERFACTUAL_SCHEMA_VERSION,
    commitmentId,
    metricKey,
    unit: typeof input.unit === 'string' ? input.unit : '',
    direction: input.direction,
    commitmentDate: input.commitmentDate,
    asOf: input.actual.asOf,
    attributionWindow,
    actualValue,
    valuePerUnitUsd: input.valuePerUnitUsd,
    annualizationFactor,
    counterfactual: freezeCounterfactual(counterfactual),
    attributedImpact,
    attributionBandBasis,
    reconciliation,
    confounds: Object.freeze([]),
    cleanWindow: attributionWindow,
    attributionStatus: 'attributed',
    provenance,
    verdictSentence,
  });

  return { status: 'ok', outcome };
}

function buildAttributionBandBasis(counterfactual: CounterfactualBasis): string {
  const via =
    counterfactual.method === 'flat_hold'
      ? 'flat-hold do-nothing baseline'
      : counterfactual.formulaRef ?? counterfactual.method;
  return (
    `Attributed impact = actual − counterfactual, where the counterfactual is the ` +
    `do-nothing projection (${via}) pinned as-of ${counterfactual.pinnedAsOf}. The ` +
    `counterfactual's Conservative/Base/Stretch uncertainty is carried as the ` +
    `attribution honesty band.`
  );
}

function buildVerdictSentence(params: {
  attributedImpact: AttributionBand;
  actualValue: number;
  counterfactual: CounterfactualBasis;
  direction: AttributionMetricDirection;
}): string {
  const { attributedImpact, actualValue, counterfactual, direction } = params;
  const dollars = (usd: number): string =>
    `${usd < 0 ? '-' : ''}$${Math.abs(usd).toLocaleString('en-US')}`;
  const cf = counterfactual.projected;
  const negative = attributedImpact.baseUsd < 0;
  const lead = negative
    ? 'Improvement is NOT attributable to the action'
    : 'Improvement attributable to the action';
  return (
    `${lead}: ${dollars(attributedImpact.conservativeUsd)} to ` +
    `${dollars(attributedImpact.stretchUsd)} (base ${dollars(attributedImpact.baseUsd)}), ` +
    `scenario-labeled — actual ${actualValue.toLocaleString('en-US')} vs do-nothing ` +
    `${cf.conservativeValue.toLocaleString('en-US')}–${cf.stretchValue.toLocaleString('en-US')} ` +
    `[${direction} metric].`
  );
}

function freezeCounterfactual(counterfactual: CounterfactualBasis): CounterfactualBasis {
  return Object.freeze({
    method: counterfactual.method,
    formulaId: counterfactual.formulaId ?? null,
    formulaVersion: counterfactual.formulaVersion ?? null,
    formulaRef: counterfactual.formulaRef ?? null,
    pinnedAsOf: counterfactual.pinnedAsOf,
    horizonAsOf: counterfactual.horizonAsOf,
    projected: Object.freeze({
      conservativeValue: counterfactual.projected.conservativeValue,
      baseValue: counterfactual.projected.baseValue,
      stretchValue: counterfactual.projected.stretchValue,
    }),
    bandBasis: counterfactual.bandBasis,
    provenance: Object.freeze({
      sourceClass: counterfactual.provenance?.sourceClass ?? 'unspecified',
      evidenceRefIds: dedupe(counterfactual.provenance?.evidenceRefIds ?? []),
      sourceArtifactId: counterfactual.provenance?.sourceArtifactId ?? null,
    }),
  });
}

// Builds a flat-hold counterfactual: the maximally-conservative do-nothing where
// the metric holds at its commitment-date baseline. A point band (all three equal)
// unless an uncertainty spread is supplied.
export function flatHoldCounterfactual(input: {
  baselineValue: number;
  pinnedAsOf: string;
  horizonAsOf: string;
  spreadUnits?: number;
  provenance: CounterfactualProvenance;
}): CounterfactualBasis {
  const spread = isFiniteNumber(input.spreadUnits) ? Math.abs(input.spreadUnits) : 0;
  return Object.freeze({
    method: 'flat_hold',
    formulaId: null,
    formulaVersion: null,
    formulaRef: null,
    pinnedAsOf: input.pinnedAsOf,
    horizonAsOf: input.horizonAsOf,
    projected: Object.freeze({
      conservativeValue: input.baselineValue - spread,
      baseValue: input.baselineValue,
      stretchValue: input.baselineValue + spread,
    }),
    bandBasis:
      spread > 0
        ? `Do-nothing = commitment-date baseline held flat, ±${spread} unit uncertainty.`
        : 'Do-nothing = commitment-date baseline held flat (point counterfactual).',
    provenance: Object.freeze({
      sourceClass: input.provenance.sourceClass,
      evidenceRefIds: dedupe(input.provenance.evidenceRefIds),
      sourceArtifactId: input.provenance.sourceArtifactId ?? null,
    }),
  });
}

function gap(
  commitmentId: string,
  metricKey: string,
  reasonCode: AttributionGapReasonCode,
  missingKeys: readonly string[],
  message: string,
): AttributionGap {
  return Object.freeze({
    status: 'gap',
    commitmentId,
    metricKey,
    reasonCode,
    missingKeys: Object.freeze([...missingKeys]),
    message,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function round(value: number, digits: number): number {
  if (!Number.isFinite(value)) return value;
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

function dedupe(values: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    if (typeof value === 'string' && value.length > 0 && !seen.has(value)) {
      seen.add(value);
      out.push(value);
    }
  }
  return Object.freeze(out);
}

export function getAttributionCounterfactualImplementationDigestInputs(): readonly Readonly<{
  name: string;
  source: string;
}>[] {
  return Object.freeze([
    { name: 'computeCounterfactualAttribution', source: String(computeCounterfactualAttribution) },
    { name: 'buildAttributionBandBasis', source: String(buildAttributionBandBasis) },
    { name: 'buildVerdictSentence', source: String(buildVerdictSentence) },
    { name: 'freezeCounterfactual', source: String(freezeCounterfactual) },
    { name: 'gap', source: String(gap) },
    { name: 'isRecord', source: String(isRecord) },
    { name: 'isFiniteNumber', source: String(isFiniteNumber) },
    { name: 'round', source: String(round) },
    { name: 'dedupe', source: String(dedupe) },
  ]);
}
